"use client";

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { white, accentColorPrimary, borderColorPrimary, boxShadowRaised } from "@churchofjesuschrist/eden-style-constants";
import { A11y } from "@churchofjesuschrist/eden-buttons";
import { jsx as _jsx } from "react/jsx-runtime";
const cubicBezier = "cubic-bezier(0,0.5,0.5,1)";
const toggleWidth = "2.625rem";
const handleSizeSmall = "1.125rem";
const handleSizeDefault = "1.375rem";
const ToggleHandle = styled.span.withConfig({
  componentId: "sc-1bldwon-0"
})(["display:block;transition:margin-inline-start 200ms ", ";height:100%;width:", ";background:", ";box-shadow:", ";border-radius:50%;"], cubicBezier, ({
  $small
}) => $small ? handleSizeSmall : handleSizeDefault, white, boxShadowRaised);
const ToggleWrapper = styled(A11y).withConfig({
  componentId: "sc-1bldwon-1"
})(["contain:strict;overflow:hidden;width:", ";height:", ";transition:background-color 200ms ", ",border-color 200ms ", ";background-color:", ";border-radius:15px;border:1px solid transparent;color:", ";&[aria-pressed=\"true\"]{background-color:currentcolor;border-color:currentcolor;", "{margin-inline-start:", ";box-shadow:inset 0 0 0 1px ", ",", ";}&:disabled{", "{box-shadow:none;}}}&:hover ", "{background:", ";}&:disabled{opacity:0.38;", "{box-shadow:none;}}"], toggleWidth, ({
  $small
}) => $small ? "1.25rem" : "1.5rem", cubicBezier, cubicBezier, borderColorPrimary, accentColorPrimary, ToggleHandle, ({
  $small
}) => `calc(100% - ${$small ? handleSizeSmall : handleSizeDefault})`, white, boxShadowRaised, ToggleHandle, ToggleHandle, white, ToggleHandle);

/**
 * A toggle switch to immediately change between two states.
 * @param {object} props - The component props
 * @param {boolean} [props.on] - Whether toggle is on
 * @param {boolean} [props.small] - Whether to apply smaller styles
 * @param {boolean} [props.disabled] - Whether to disable the toggle
 * @param {string} [props."aria-label"] - Defines a value that labels the current element. If another element is visible in your UI that already labels the purpose of the `Toggle`, use the 'aria-labelledby' prop instead.
 * @param {string} [props."aria-labelledby"] - Identifies the element(s) that label(s) the `Toggle`. Must be a space-separated list of one or more `id` values referencing the element(s) present in the UI that label(s) the current element. If no accompanying visible label(s) exist, you should instead use the 'aria-label' or 'title' prop instead.
 * @param {string} [props.title] - Defines a value that labels the current element. If another element is visible in your UI that already labels the purpose of the `Toggle`, use the 'aria-labelledby' prop instead.
 * @param {Function} [props.onToggle] - Handler called after a toggle occurs. Receives a boolean of whether it will now be on. If the handler returns an explicit `true` or `false`, this will be the new "on" value
 * @returns {React.ReactElement} The Toggle component
 */
const Toggle = ({
  on: propsOn,
  small,
  disabled,
  "aria-labelledby": ariaLabelledBy,
  "aria-label": ariaLabel,
  onToggle = () => {},
  title,
  ...rest
}) => {
  const [on, setOn] = useState(propsOn);
  useEffect(() => {
    setOn(propsOn);
  }, [propsOn]);

  // If a given `onToggle` prop returns an explicit `true` or `false`, use that value
  // as the next `on` state. Otherwise, proceed as normal.
  const toggle = () => {
    let nextOn = !on;
    const override = onToggle(nextOn);
    if (typeof override === "boolean") {
      nextOn = override;
    }
    setOn(nextOn);
  };
  return /*#__PURE__*/_jsx(ToggleWrapper, {
    ...rest,
    $small: small,
    disabled: disabled,
    "aria-pressed": on ? "true" : "false",
    "aria-labelledby": ariaLabelledBy,
    "aria-label": ariaLabel,
    title: title,
    onClick: toggle,
    children: /*#__PURE__*/_jsx(ToggleHandle, {
      $small: small
    })
  });
};
Toggle.displayName = "Toggle";
export default Toggle;